import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as d3 from 'd3';
import { backendlink } from '../../config';
import './ECGScatterPlot.css'; // Optional: for styling
import { LoadIndicator } from 'devextreme-react';

const ECGScatterPlot = ({ uid, patientToken, user, handleSetFiveMinIntervalID, popupSize }) => {
    const svgRef = useRef();
    const [loading, setLoading] = useState(false);

    const renderGraph = (data = []) => {
        const container = svgRef.current.parentElement;
        const width = container.clientWidth;
        const height = 400;
        const margin = { top: 50, right: 30, bottom: 40, left: 60 };

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove(); // Clear previous renders


        // Get min and max heart rate from data
        const [minHr, maxHr] = d3.extent(data, d => d.avrHr);

        const xScale = d3.scaleLinear()
            .domain([Math.floor(minHr / 5) * 5, Math.ceil(maxHr / 5) * 5]) // round to nearest 5
            .range([margin.left, width - margin.right]);


        const yScale = d3.scaleLinear()
            .domain([0, 1])
            .range([height - margin.bottom, margin.top]);

        // Add X grid lines
        svg.append('g')
            .attr('class', 'grid')
            .attr('transform', `translate(0,${height - margin.bottom})`)
            .call(
                d3.axisBottom(xScale)
                    .ticks(10)
                    .tickSize(-(height - margin.top - margin.bottom))
                    .tickFormat('')
            );

        // Add Y grid lines
        svg.append('g')
            .attr('class', 'grid')
            .attr('transform', `translate(${margin.left},0)`)
            .call(
                d3.axisLeft(yScale)
                    .ticks(10)
                    .tickSize(-(width - margin.left - margin.right))
                    .tickFormat('')
            );

        // X Axis
        svg.append('g')
            .attr('transform', `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(xScale).ticks(10));

        // Y Axis
        svg.append('g')
            .attr('transform', `translate(${margin.left},0)`)
            .call(d3.axisLeft(yScale).ticks(10));


        svg.append('text')
            .attr('x', width / 2)
            .attr('y', margin.top / 2)
            .attr('text-anchor', 'middle')
            .style("font-family", "'Open Sans', sans-serif")
            .style("font-weight", "600")
            .style("font-size", "20px")
            .style("letter-spacing", "0px")
            .style("fill", "#181C23")
            .text('AF Probability vs Heart Rate');


        // X Axis Label
        svg.append('text')
            .attr('x', width / 2)
            .attr('y', height - 5)
            .attr('text-anchor', 'middle')
            .attr('font-size', '14px')
            .style("font-family", "'Open Sans', sans-serif")
            .text('Heart Rate (BPM)');

        // Y Axis Label
        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -height / 2)
            .attr('y', 25)
            .attr('text-anchor', 'middle')
            .style("font-family", "'Open Sans', sans-serif")
            .attr('font-size', '14px')
            .text('AF Probability');

        svg.selectAll('circle')
            .data(data)
            .enter()
            .append('circle')
            .attr('cx', d => xScale(d.avrHr))
            .attr('cy', d => yScale(d.afProb))
            .attr('r', 5)
            .attr('fill', d => d.risk ? 'red' : 'steelblue') // highlight risky points
            .style('cursor', 'pointer')
            .on('click', (event, d) => {
                handlePointClick(d.id, uid); // trigger your click handler
            });
    };


    const fetchScatterplotData = async () => {
        setLoading(true);
        try {
            console.log('user', user);
            const response = await axios.post(
                `${backendlink}doctor/getpatientecgscatterplot`,
                {
                    ecgrecordid: uid,
                    patienttoken: patientToken,
                },
                {
                    headers: {
                        token: user.token,
                    },
                }
            );

            if (response.data.errorno === 0) {
                // console.log("sdskjaldsa", response.data.result)
                // renderGraph(response.data.result);
                const filteredData = response.data.result.filter(
                    d => d.afProb !== undefined && d.avrHr !== undefined
                );
    
                renderGraph(filteredData);
            }

        } catch (error) {
            console.error('Error fetching scatterplot data:', error);
        }
        finally {
            setLoading(false);
        }
    };

    const handlePointClick = (id, uid) => {
        handleSetFiveMinIntervalID(id);
    }

    useEffect(() => {
        if (uid) {
            fetchScatterplotData();
        }
    }, [uid, popupSize]);

    return <div id="scatterplot-container" style={{ width: '100%', height: '100%' }}>
        {loading && <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px', // Adjust height to center vertically
                width: '100%', // Full width
            }}
        >
            <LoadIndicator
                width={50}
                height={50}
            />
        </div>
        }
        
        <svg ref={svgRef} style={{ width: '100%', height: '100%', display: loading ? 'none' : 'block' }} preserveAspectRatio="xMidYMid meet"></svg>
    </div>;
};

export default ECGScatterPlot;